import React from 'react';
import { Leaf } from 'lucide-react';
import Logo from '../Logo';
import MediaCarousel, { MediaItem } from '../MediaCarousel';

interface HeroSectionProps {
  headline: string;
  subheadline: string;
  ctaText: string;
  ctaAction: () => void;
  mediaItems?: MediaItem[];
  showMediaCarousel?: boolean;
  badges?: string[];
  showLogo?: boolean;
  backgroundColor?: string;
}

export default function HeroSection({
  headline,
  subheadline,
  ctaText,
  ctaAction,
  mediaItems,
  showMediaCarousel = true,
  badges,
  showLogo = true,
  backgroundColor = 'bg-white'
}: HeroSectionProps) {
  return (
    <section className={`${backgroundColor} py-12 md:py-16`}>
      <div className="container mx-auto px-4">
        {showLogo && (
          <div className="flex justify-center mb-8">
            <Logo size="lg" showText={false} variant="full" />
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center max-w-6xl mx-auto">
          {/* Left Column - Text Content */}
          <div className="order-2 md:order-1">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900">
              {headline}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-6">
              {subheadline}
            </p>

            {badges && badges.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-6">
                {badges.map((badge, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-4">
              <button
                onClick={ctaAction}
                className="bg-primary-500 text-white py-4 px-8 rounded-lg hover:bg-primary-600 transition-all duration-300 font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                <Leaf className="h-6 w-6" />
                {ctaText}
              </button>
            </div>
          </div>

          {/* Right Column - Media */}
          {showMediaCarousel && mediaItems && (
            <div className="order-1 md:order-2">
              <div className="w-full">
                <MediaCarousel
                  items={mediaItems}
                  autoPlayInterval={5000}
                  showControls={true}
                  className="shadow-lg rounded-lg"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
